import { Component } from '@theme/component';
import { clamp, prefersReducedMotion } from '@theme/utilities';

/** Distance in px a pointer must travel horizontally before a press becomes a drag. */
const DRAG_THRESHOLD = 8;

/** Pointer speed in px/ms above which a release is treated as a flick. */
const FLICK_VELOCITY = 0.35;

/** Fraction of the drag distance applied once dragging past the first or last slide. */
const RUBBER_BAND = 3;

/**
 * A contained product carousel.
 *
 * Unlike `slideshow-component`, every slide is fully inside the visible area: the track is
 * translated by whole slides and clamped so the last slide's edge lines up with the end of the
 * viewport. Navigation moves one slide at a time and the viewport is sized to the section's
 * content width, so nothing bleeds past the page margins.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} viewport
 * @property {HTMLElement} track
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class ProductListCarousel extends Component {
  requiredRefs = ['viewport', 'track'];

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** Distance in px between the start of one slide and the start of the next. */
  #step = 0;

  /** Largest scroll offset in px, i.e. the offset that aligns the last slide to the right edge. */
  #maxOffset = 0;

  /** Index of the current slide. */
  #index = 0;

  /** Offset in px currently applied to the track. */
  #offset = 0;

  /** Set while a drag is in progress so the click that follows can be swallowed. */
  #dragged = false;

  connectedCallback() {
    super.connectedCallback();

    // Product card galleries are themselves slideshows. They are disabled inside a carousel so
    // their drag handling doesn't compete with ours — the same behaviour they had when this
    // carousel was built on `slideshow-component`.
    for (const gallery of this.querySelectorAll('slideshow-component')) {
      gallery.setAttribute('disabled', 'true');
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { viewport } = this.refs;

    viewport.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    viewport.addEventListener('click', this.#handleClick, { capture: true, signal });
    viewport.addEventListener('focusin', this.#handleFocusIn, { signal });
    viewport.addEventListener('scroll', this.#handleScroll, { signal });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    }

    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(viewport);

    const firstSlide = this.slides[0];
    if (firstSlide) this.#resizeObserver.observe(firstSlide);

    this.#measure();
    this.#goTo(this.#index, { animate: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = undefined;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
  }

  /**
   * Re-measure after the Section Rendering API morphs new markup into place.
   */
  updatedCallback() {
    super.updatedCallback();

    this.#measure();
    this.#goTo(this.#index, { animate: false });
  }

  /** @returns {HTMLElement[]} */
  get slides() {
    return this.refs.slides ?? [];
  }

  /** Index of the last reachable slide. Beyond it the track would show empty space. */
  get lastIndex() {
    if (this.#step <= 0) return 0;

    return Math.max(0, Math.ceil(this.#maxOffset / this.#step));
  }

  get loop() {
    return this.hasAttribute('loop');
  }

  get dragEnabled() {
    return this.hasAttribute('draggable-slides') && this.#maxOffset > 0;
  }

  /**
   * Moves to the previous slide.
   * @param {Event} [event]
   */
  previous(event) {
    event?.preventDefault();

    const target = this.#index - 1;
    this.#goTo(target < 0 ? (this.loop ? this.lastIndex : 0) : target);
  }

  /**
   * Moves to the next slide.
   * @param {Event} [event]
   */
  next(event) {
    event?.preventDefault();

    const target = this.#index + 1;
    this.#goTo(target > this.lastIndex ? (this.loop ? 0 : this.lastIndex) : target);
  }

  /**
   * Reads the slide geometry off the DOM.
   *
   * `--pc-per-view` drives the slide width in CSS, so measuring rendered boxes keeps the JS in
   * step with whatever the media queries resolved to.
   */
  #measure() {
    const { viewport, track } = this.refs;
    const slides = this.slides;
    const [first, second] = slides;
    const last = slides[slides.length - 1];

    if (first && last) {
      const firstRect = first.getBoundingClientRect();

      // Rect deltas keep sub-pixel precision and are unaffected by the track's translation.
      this.#step = second ? Math.abs(second.getBoundingClientRect().left - firstRect.left) : firstRect.width;

      // Layout positions, so this stays correct while the track is mid-transition.
      const contentWidth = last.offsetLeft - track.offsetLeft + last.offsetWidth;
      this.#maxOffset = Math.max(0, contentWidth - viewport.clientWidth);
    } else {
      this.#step = 0;
      this.#maxOffset = 0;
    }

    this.toggleAttribute('no-scroll', this.#maxOffset <= 0);
    this.#alignArrows();
  }

  /**
   * Centres the arrows on the product image rather than on the whole card, which includes the
   * title and price. Falls back to the CSS default when there is no media to measure.
   */
  #alignArrows() {
    const media = this.slides[0]?.querySelector('.product-media-container, img, svg');
    if (!media) return;

    const mediaRect = media.getBoundingClientRect();
    if (!mediaRect.height) return;

    const center = mediaRect.top - this.getBoundingClientRect().top + mediaRect.height / 2;
    this.style.setProperty('--pc-arrow-top', `${Math.round(center)}px`);
  }

  /**
   * Moves the track to a slide index.
   * @param {number} index
   * @param {Object} [options]
   * @param {boolean} [options.animate=true]
   */
  #goTo(index, { animate = true } = {}) {
    this.#index = clamp(index, 0, this.lastIndex);

    this.#setOffset(Math.min(this.#index * this.#step, this.#maxOffset), {
      animate: animate && !prefersReducedMotion(),
    });
    this.#updateControls();
  }

  /**
   * Translates the track.
   * @param {number} offset
   * @param {Object} [options]
   * @param {boolean} [options.animate=true]
   */
  #setOffset(offset, { animate = true } = {}) {
    this.#offset = offset;
    this.toggleAttribute('animating', animate);
    this.refs.track.style.setProperty('--pc-offset', `${-offset}px`);
  }

  /**
   * Syncs the arrows and flags which slides are fully in view.
   *
   * Out-of-view slides are deliberately left in the accessibility tree and in the tab order —
   * `#handleFocusIn` brings them into view instead, so keyboard users never land on a card they
   * can't see and no focusable content ends up inside an `aria-hidden` subtree.
   */
  #updateControls() {
    const { previous, next } = this.refs;
    const { lastIndex, loop } = this;

    if (previous) previous.disabled = !loop && this.#index <= 0;
    if (next) next.disabled = !loop && this.#index >= lastIndex;

    const { track, viewport } = this.refs;
    const viewportWidth = viewport.clientWidth;

    for (const slide of this.slides) {
      // `offsetLeft` is layout-based, so it stays correct while the track is mid-transition.
      const start = slide.offsetLeft - track.offsetLeft;
      // A couple of pixels of tolerance absorbs rounding on fractional slide widths.
      const visible = start >= this.#offset - 2 && start + slide.offsetWidth <= this.#offset + viewportWidth + 2;

      slide.toggleAttribute('visible', visible);
    }
  }

  #handleResize = () => {
    this.#measure();
    this.#goTo(this.#index, { animate: false });
  };

  /**
   * The viewport is a scroll container, so the browser may scroll it to reveal a focused card.
   * Undo that and translate the track instead, keeping the two in sync.
   */
  #handleScroll = () => {
    const { viewport } = this.refs;
    if (viewport.scrollLeft !== 0) viewport.scrollLeft = 0;
  };

  /** @param {FocusEvent} event */
  #handleFocusIn = (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;

    const slide = this.slides.find((candidate) => candidate.contains(target));
    if (!slide) return;

    // Only move if the focused slide isn't already fully in view.
    if (slide.hasAttribute('visible')) return;

    this.#goTo(this.slides.indexOf(slide));
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    const { target } = event;
    if (!(target instanceof Element) || !this.contains(target)) return;

    const slide = this.slides.find((candidate) => candidate.contains(target));
    if (!slide) return;

    this.#goTo(this.slides.indexOf(slide));
  };

  /**
   * Swallows the click that a drag would otherwise fire on the product card underneath.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!this.#dragged) return;

    this.#dragged = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    this.#dragged = false;

    if (!this.dragEnabled) return;
    if (event.button > 0) return;
    // Let interactive controls inside a card (quick add, variant swatches) handle their own press.
    if (event.target instanceof Element && event.target.closest('button, select, input, model-viewer')) return;

    const { viewport } = this.refs;
    const controller = new AbortController();
    const { signal } = controller;

    const startX = event.clientX;
    const startY = event.clientY;
    const startOffset = this.#offset;

    let dragging = false;
    let previousX = startX;
    let previousTime = event.timeStamp;
    let velocity = 0;

    /** @param {PointerEvent} event */
    const onPointerMove = (event) => {
      const deltaX = event.clientX - startX;

      if (!dragging) {
        // Ignore vertical gestures so the page can still be scrolled from over the carousel.
        if (Math.abs(deltaX) < DRAG_THRESHOLD || Math.abs(deltaX) <= Math.abs(event.clientY - startY)) return;

        dragging = true;
        this.#dragged = true;
        this.setAttribute('dragging', '');
        viewport.setPointerCapture(event.pointerId);
      }

      const elapsed = event.timeStamp - previousTime;
      if (elapsed > 0) velocity = (event.clientX - previousX) / elapsed;

      previousX = event.clientX;
      previousTime = event.timeStamp;

      this.#setOffset(this.#resist(startOffset - deltaX), { animate: false });
    };

    /** @param {PointerEvent} event */
    const onPointerUp = (event) => {
      controller.abort();

      if (!dragging) return;

      this.removeAttribute('dragging');
      if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);

      const settled = this.#offset / (this.#step || 1);
      // A flick commits to the next slide in the direction of travel; a slow drag snaps to
      // whichever slide is closest.
      const index =
        Math.abs(velocity) > FLICK_VELOCITY
          ? velocity < 0
            ? Math.ceil(settled)
            : Math.floor(settled)
          : Math.round(settled);

      this.#goTo(index);
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  };

  /**
   * Applies resistance when dragged past either end.
   * @param {number} offset
   * @returns {number}
   */
  #resist(offset) {
    if (offset < 0) return offset / RUBBER_BAND;
    if (offset > this.#maxOffset) return this.#maxOffset + (offset - this.#maxOffset) / RUBBER_BAND;

    return offset;
  }
}

if (!customElements.get('product-list-carousel-component')) {
  customElements.define('product-list-carousel-component', ProductListCarousel);
}
